import React, { type FC, useEffect } from 'react'
import { Box, Grid, type SxProps, type Theme } from '@mui/material'

import { Capability, createIsAuthenticatedWithCapabilitiesSelector, isTranslation, selectExplorerError, Translation } from '../../../domain'

import { DetailsHeader } from '../common'
import { ErrorMessage } from '../ErrorMessage'
import { boldFormatter, createFormatter, FormattedTranslation } from '../Internationalization'
import { AbsoluteRouteOnlyPath, PermissionedLink } from '../routing'
import { useAppState, useAppUsecase } from '../State'
import { AvailableTopics } from './AvailableTopics'
import { History } from './History'
import { MonitoredTopics } from './MonitoredTopics'

const spacing = 4

const leftColumnStyle: SxProps<Theme> = { p: spacing, borderRight: '1px solid', borderColor: 'grey.300' }
const monitoredRowStyle: SxProps<Theme> = { p: spacing, borderBottom: '1px solid', borderColor: 'grey.300' }
const historyRowStyle: SxProps<Theme> = { p: spacing }
const missingReadTopicsPermissionStyle: SxProps<Theme> = { py: spacing }
const linkStyle = { color: 'info.main' }

const selectHasPermissionToReadTopicsMetadata = createIsAuthenticatedWithCapabilitiesSelector(Capability.TOPIC_CHUNKS_READ)

export const Explorer: FC = () => {
    const error = useAppState(selectExplorerError)
    const hasPermissionToReadTopicsMetadata = useAppState(selectHasPermissionToReadTopicsMetadata)

    const startExplorer = useAppUsecase('startExplorerSubscriptionUsecase')
    useEffect(() => startExplorer.invoke(), [startExplorer])

    if (error !== null) {
        return (
            <ErrorMessage
                data-testid="explorer-general-error"
                error={error}
                stack
                extras="section"
                customTitle={(e) => (isTranslation(e.message) ? <FormattedTranslation id={e.message} /> : null)}
            />
        )
    }

    return (
        <Grid container>
            <Grid size={{ xs: 12 }}>
                <DetailsHeader
                    title={Translation.DATA_EXPLORER}
                    description={[Translation.DATA_EXPLORER_DOCUMENTATION_TITLE, Translation.DATA_EXPLORER_DOCUMENTATION_BODY]}
                />
            </Grid>
            {!hasPermissionToReadTopicsMetadata && (
                <Grid size={{ xs: 12 }}>
                    <Box sx={missingReadTopicsPermissionStyle} data-testid="missing-topics-read-permissions">
                        <FormattedTranslation
                            id={Translation.TOPICS_METADATA_PERMISSION_MISSING}
                            values={{
                                b: boldFormatter,
                                link: createFormatter(PermissionedLink, { path: AbsoluteRouteOnlyPath.SERVICES, sx: linkStyle }),
                            }}
                        />
                    </Box>
                </Grid>
            )}

            <Grid size={{ xs: 4 }} sx={leftColumnStyle}>
                <AvailableTopics />
            </Grid>
            <Grid size={{ xs: 8 }}>
                <Grid container>
                    <Grid size={{ xs: 12 }} sx={monitoredRowStyle}>
                        <MonitoredTopics />
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={historyRowStyle}>
                        <History />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}
